import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { OperandRow } from './OperandRow'
import { MATH_ACCENT } from './operatorTheme'
import { mathSymbol } from '../../lib/mathOps'
import { selectedBorder } from './selectedBorder.js'

/**
 * Math operators — one component for all four node types (+ - * /), keyed by
 * `type` (same pattern as ComparisonNode / LogicalNode). Two value inputs
 * (A, B) → a NUMBER on out-value. Each input is a pin OR an inline literal box
 * (see OperandRow); operands coerce to numbers (null/empty/non-numeric → 0),
 * arithmetic in src/lib/mathOps.js. The operator is the NODE TYPE, not editable
 * config — pick the specific node from the palette.
 *
 * The one editable field is the int/float toggle in the header (data.resultType):
 * float (default) leaves the result as-is; int truncates toward zero, so
 * `counter + 1` stays an int. Divide-by-zero shows ÷0 in the preview (returns 0).
 */

const OP = {
  add:      { label: 'Add' },
  subtract: { label: 'Subtract' },
  multiply: { label: 'Multiply' },
  divide:   { label: 'Divide' },
}

const ACCENT = MATH_ACCENT
const HEADER = '#1c1430'
const WILDCARD = '#b0b0b0'   // any-type input pin (coerced to number)
const NUMBER   = '#9fd35a'   // number output pin (Unreal float-green convention)

const stop = e => e.stopPropagation()

export function MathNode({ id, type, data, selected }) {
  const meta    = OP[type] ?? OP.add
  const sym     = mathSymbol(type)
  const fired   = isFired(data)
  const preview = data._preview   // set by the runtime each time the node resolves
  const isDivZero = preview === '÷0'

  return (
    <div style={{
      background: selected ? '#1a1230' : '#0e0a1a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#3a2a5a') : '#3a2a5a'}`,
      borderRadius: 6, minWidth: 190, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: HEADER,
        padding: '7px 14px 7px 16px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${ACCENT}33`, borderRadius: '4px 4px 0 0',
        position: 'relative',
      }}>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 13, fontFamily: 'var(--m-mono)' }}>{sym}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{meta.label}</span>
        {/* Int/float toggle — the only editable field. int truncates toward zero. */}
        <select
          className="nodrag nowheel"
          value={data.resultType ?? 'float'}
          aria-label="result type"
          onChange={e => data.onChange?.('resultType', e.target.value)}
          onPointerDown={stop} onMouseDown={stop} onClick={stop}
          style={{
            marginLeft: 'auto', background: '#02100d', border: `1px solid ${ACCENT}55`,
            color: '#dfe', borderRadius: 3, fontSize: 9, fontFamily: 'var(--m-mono)', padding: '2px 4px', outline: 'none',
          }}>
          <option value="float">float</option>
          <option value="int">int</option>
        </select>
        {/* Output (number) pin — ReactFlow's default right-edge placement. */}
        <Handle type="source" position={Position.Right} id="out-value"
          style={{ background: NUMBER, width: 10, height: 10, borderRadius: '50%' }} />
      </div>

      {['a', 'b'].map(pin => (
        <OperandRow key={pin}
          nodeId={id} pinId={`in-${pin}`} label={pin.toUpperCase()}
          accent={ACCENT} pinColor={WILDCARD} borderColor="#241a3a"
          value={data[pin]} onChange={v => data.onChange?.(pin, v)} />
      ))}

      {preview !== undefined && (
        <div style={{ padding: '5px 14px 7px 16px', color: isDivZero ? '#e08080' : '#c7b0e6', fontSize: 10 }}>
          → {String(preview)}
        </div>
      )}
    </div>
  )
}
